// src/pages/EXIFPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { parseNovelAIMetadata } from "./novelaiMeta";
import KakaoAdFit from "../../components/KakaoAdFit";
import "./EXIFPage.css";

function EXIFPage() {
  const [darkMode, setDarkMode] = useState(false);

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(false);

  // 좌측 패널 상태
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewHeight, setPreviewHeight] = useState(400);

  // 탭 상태: 초기값 "NovelAI"
  const [activeTab, setActiveTab] = useState("NovelAI");

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const EMPTY_TEXT = "정보를 찾을 수 없습니다.";

  // NAI: 각 필드별 출력 상태
  const [naiPositiveDump, setNaiPositiveDump] = useState(EMPTY_TEXT);
  const [naiCharPositiveDump, setNaiCharPositiveDump] = useState(EMPTY_TEXT);
  const [naiNegativeDump, setNaiNegativeDump] = useState(EMPTY_TEXT);
  const [naiCharNegativeDump, setNaiCharNegativeDump] = useState(EMPTY_TEXT);
  const [naiModelDump, setNaiModelDump] = useState(EMPTY_TEXT);
  const [naiStealthDump, setNaiStealthDump] = useState(EMPTY_TEXT);

  // 현재 페이지 canonical URL (CSR 기준)
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI 이미지 EXIF 추출기",
      description:
        "PNG·WebP 이미지의 EXIF·텍스트 메타데이터를 무료로 추출하는 TooLoves의 AI 이미지 EXIF 추출기",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "PNG, WebP, 무료, EXIF 추출, 메타데이터, NovelAI, 스텔스 PNG, 프롬프트 추출, TooLoves",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "KRW",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // 브라우저/모바일 네비게이션 바 색상
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const el = document.createElement("meta");
      el.name = "theme-color";
      el.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(el);
    }
  }, [darkMode]);

  // 모바일 환경 감지: UA + matchMedia(pointer, width)
  useEffect(() => {
    const getIsMobile = () => {
      if (typeof window === "undefined") return false;
      const ua = navigator.userAgent || navigator.vendor || "";
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      const supportsMatch = typeof window.matchMedia === "function";
      const coarse = supportsMatch ? window.matchMedia("(pointer: coarse)").matches : false;
      const maxWidth = supportsMatch ? window.matchMedia("(max-width: 820px)").matches : false;

      // UA 기반 OR 터치 주 입력(coarse) AND 적정 폭
      return uaMobile || (coarse && maxWidth);
    };

    const apply = () => setIsMobile(getIsMobile());
    apply();

    const onChange = () => apply();

    // 리스너 등록
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    const mqlCoarse = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)") : null;
    const mqlMax = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 820px)") : null;

    mqlCoarse && mqlCoarse.addEventListener && mqlCoarse.addEventListener("change", onChange);
    mqlMax && mqlMax.addEventListener && mqlMax.addEventListener("change", onChange);

    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
      mqlCoarse && mqlCoarse.removeEventListener && mqlCoarse.removeEventListener("change", onChange);
      mqlMax && mqlMax.removeEventListener && mqlMax.removeEventListener("change", onChange);
    };
  }, []);

  // 파일 업로드 처리(UI만)
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    const imgs = arr.filter((f) => f.type.startsWith("image/"));
    const withId = imgs.map((f) => ({
      id: `${Date.now()}_${Math.random()}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setFiles((prev) => {
      const merged = [...prev, ...withId];
      return merged;
    });
    if (withId.length > 0) setSelectedId(withId[withId.length - 1].id);
  }, []);

  const onPickClick = useCallback(() => fileInputRef.current?.click(), []);
  const onFileChange = useCallback(
    (e) => {
      if (e.target.files?.length) addFiles(e.target.files);
      // 동일 파일 재업로드 허용
      e.target.value = "";
    },
    [addFiles]
  );

  const onRemove = useCallback((id) => {
    setFiles((prev) => prev.filter((x) => x.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const onRemoveDuplicates = useCallback(() => {
    setFiles((prev) => {
      const seen = new Set();
      const deduped = [];
      for (let i = prev.length - 1; i >= 0; i--) {
        const name = prev[i].file.name;
        if (!seen.has(name)) {
          seen.add(name);
          deduped.unshift(prev[i]);
        }
      }
      return deduped;
    });
  }, []);

  const onReset = useCallback(() => {
    setFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setSelectedId(null);
    setNaiPositiveDump(EMPTY_TEXT);
    setNaiCharPositiveDump(EMPTY_TEXT);
    setNaiNegativeDump(EMPTY_TEXT);
    setNaiCharNegativeDump(EMPTY_TEXT);
    setNaiModelDump(EMPTY_TEXT);
    setNaiStealthDump(EMPTY_TEXT);
  }, []);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) || null,
    [files, selectedId]
  );

  // 안전 JSON 파서
  const tryParseJSON = useCallback((text) => {
    if (!text || typeof text !== "string") return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, []);

  // 스텔스 원본에서 값 추출
  const extractFromStealth = useCallback(
    (out) => {
      const raw = out?.stealth?.raw || "";
      const rootObj = tryParseJSON(raw);
      const commentStr =
        (rootObj && typeof rootObj.Comment === "string" && rootObj.Comment) || "";
      const commentObj = tryParseJSON(commentStr) || {};

      // 기본/캐릭터 프롬프트
      const pos = (commentObj.prompt || "").trim();
      const charPos = (commentObj?.v4_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // 부정 프롬프트
      const neg =
        (commentObj?.v4_negative_prompt?.caption?.base_caption || "").trim() ||
        (commentObj.uc || "").trim();

      const charNeg = (commentObj?.v4_negative_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // 모델/설정
      const steps = commentObj?.steps;
      const sampler = commentObj?.sampler;
      const noise = commentObj?.noise_schedule;
      const seed = commentObj?.seed;
      const width = commentObj?.width;
      const height = commentObj?.height;
      const scale = commentObj?.scale;
      const cfgRescale = commentObj?.cfg_rescale;

      // Source 우선순위
      const source =
        rootObj?.Source ||
        out?.pngTexts?.texts?.Source ||
        out?.webpMeta?.customTexts?.Source ||
        "";

      const modelLines = [];
      if (steps != null) modelLines.push(`Steps: ${steps}`);
      if (sampler != null) modelLines.push(`Sampler: ${sampler}`);
      if (noise != null) modelLines.push(`Noise Schedule: ${noise}`);
      if (seed != null) modelLines.push(`Seed: ${seed}`);
      if (width != null && height != null) modelLines.push(`Size: ${width} x ${height}`);
      if (scale != null) modelLines.push(`Scale: ${scale}`);
      if (cfgRescale != null) modelLines.push(`CFG Rescale: ${cfgRescale}`);
      if (source) modelLines.push(`Source: ${source}`);

      const prettyStealth = rootObj ? JSON.stringify(rootObj, null, 2) : raw || "";

      return {
        pos,
        charPos,
        neg,
        charNeg,
        modelDump: modelLines.join("\n"),
        stealthPretty: prettyStealth,
      };
    },
    [tryParseJSON]
  );

  // DnD
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      e.preventDefault();
      el.classList.add("dragover");
    };
    const onDragLeave = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
    };
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  // novelaiMeta.js 연동
  useEffect(() => {
    let cancelled = false;
    async function run() {
      // 초기화
      setNaiPositiveDump(EMPTY_TEXT);
      setNaiCharPositiveDump(EMPTY_TEXT);
      setNaiNegativeDump(EMPTY_TEXT);
      setNaiCharNegativeDump(EMPTY_TEXT);
      setNaiModelDump(EMPTY_TEXT);
      setNaiStealthDump(EMPTY_TEXT);

      if (!selected?.file) return;

      try {
        const file = selected.file;
        const ab = await file.arrayBuffer();
        const out = await parseNovelAIMetadata({
          arrayBuffer: ab,
          mime: file.type,
          imageSource: file, // stealth_pngcomp 추출 가능
        });

        // 1차: Stealth 원본에서 직접 추출
        const fromStealth = extractFromStealth(out);

        // 2차: 폴백 (기존 파서 결과 이용)
        const fallbackPos = (out?.result?.sampler?.prompts || []).join("\n").trim();
        const fallbackNeg = (out?.result?.sampler?.negative_prompts || [])
          .join("\n")
          .trim();

        const pos = fromStealth.pos || fallbackPos;
        const neg = fromStealth.neg || fallbackNeg;

        if (!cancelled) {
          setNaiPositiveDump(pos || EMPTY_TEXT);
          setNaiCharPositiveDump(fromStealth.charPos || EMPTY_TEXT);
          setNaiNegativeDump(neg || EMPTY_TEXT);
          setNaiCharNegativeDump(fromStealth.charNeg || EMPTY_TEXT);
          setNaiModelDump(fromStealth.modelDump || EMPTY_TEXT);
          setNaiStealthDump(fromStealth.stealthPretty || EMPTY_TEXT);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = `파싱 오류: ${e?.message || String(e)}`;
          setNaiPositiveDump(msg);
          setNaiNegativeDump(EMPTY_TEXT);
          setNaiCharPositiveDump(EMPTY_TEXT);
          setNaiCharNegativeDump(EMPTY_TEXT);
          setNaiModelDump(EMPTY_TEXT);
          setNaiStealthDump(EMPTY_TEXT);
        }
      }
    }
    run();
    return () => {
      let _ = cancelled; // silence eslint
      cancelled = true;
    };
  }, [selected, extractFromStealth]);

  // 공통 복사 헬퍼
  const copyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch {}
  }, []);

  const isEmpty = (v) => !v || v.trim() === "" || v === EMPTY_TEXT;

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ko" }}>
        <title>AI 이미지 EXIF 추출기 | TooLoves</title>
        <meta
          name="description"
          content="PNG·WebP 이미지의 EXIF·텍스트 메타데이터를 무료로 추출하는 AI 이미지 EXIF 추출기. NovelAI 스텔스 PNG, 프롬프트, 씨드, 모델 등 지원."
        />
        <meta
          name="keywords"
          content="PNG, WebP, 무료, EXIF 추출, 메타데이터, 이미지 메타데이터, NovelAI, 스텔스 PNG, 프롬프트 추출, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 초기 theme-color, 런타임에서 useEffect로 동기화 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="AI 이미지 EXIF 추출기 | TooLoves" />
        <meta
          property="og:description"
          content="PNG·WebP 이미지의 EXIF·텍스트 메타데이터를 무료로 추출."
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-exif.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI 이미지 EXIF 추출기 | TooLoves" />
        <meta
          name="twitter:description"
          content="PNG·WebP 이미지의 EXIF·텍스트 메타데이터를 무료로 추출."
        />
        <meta name="twitter:image" content="/og-exif.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/" className="brand-link" aria-label="damoa 홈으로 이동">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode((v) => !v)}
            aria-label="테마 전환"
          >
            {darkMode ? "라이트 모드" : "다크 모드"}
          </button>
        </div>
      </header>

      <main className="container exif-container">
        {isMobile ? (
          // 모바일 안내 전용 블록 (기존 CSS 토큰 재활용)
          <section className="generator-card" role="region" aria-label="모바일 지원 안내">
            <div className="metadata-section">
              <h3 className="section-title">모바일 지원 안내</h3>
              
              {/* ✅ 모바일 화면에서도 광고 표시 */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <KakaoAdFit />
              </div>

              <p className="hint">이 기능은 모바일에서 지원하지 않습니다.</p>
              <div className="terms-actions">
                <Link to="/" className="btn" aria-label="메인 페이지로 돌아가기">
                  메인으로 돌아가기
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* 좌측 패널 */}
            <section className="generator-card panel-left">
              <div className="upload-header">
                <h4 className="section-title">이미지 업로드</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={onFileChange}
                />
                <button
                  type="button"
                  onClick={onPickClick}
                  aria-label="이미지 파일 선택"
                  className="btn btn-primary"
                >
                  파일 선택
                </button>
              </div>

              <div ref={dropRef} id="drop-zone">
                여기에 이미지를 드래그 앤 드롭하거나, 파일 선택 버튼을 사용하세요
              </div>

              <div className="preview-header">
                <h2 className="section-title small">이미지 미리보기</h2>
                <div className="preview-size-control">
                  <input
                    type="range"
                    min={100}
                    max={1200}
                    value={previewHeight}
                    onChange={(e) => setPreviewHeight(parseInt(e.target.value, 10))}
                  />
                  <span id="preview-size-value">{previewHeight}px</span>
                </div>
              </div>

              <div className="qr-wrap">
                {selected?.url ? (
                  <img
                    className="preview-img"
                    src={selected.url}
                    alt="이미지 미리보기"
                    height={previewHeight}
                  />
                ) : (
                  <p className="placeholder">선택된 이미지가 없습니다</p>
                )}
              </div>

              <div className="actions">
                <button onClick={onRemoveDuplicates}>중복 제거</button>
                <button onClick={onReset}>초기화</button>
              </div>

              <h2 className="section-title small list-title">이미지 목록</h2>
              <ul id="image-list">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className={`file-row ${f.id === selectedId ? "selected" : ""}`}
                    onClick={() => setSelectedId(f.id)}
                  >
                    <span className="file-name">{f.file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(f.id);
                      }}
                      className="btn btn-secondary"
                    >
                      제거
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* 우측 패널 */}
            <section className="generator-card panel-right">
              {/* ✅ 우측 패널(결과창) 최상단에 광고 배치 */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                <KakaoAdFit />
              </div>

              {/* 탭 */}
              <ul className="tabs">
                {["NovelAI"].map((label) => {
                  const key = label;
                  const active = activeTab === key;
                  return (
                    <li
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={active ? "active" : ""}
                    >
                      {label}
                    </li>
                  );
                })}
              </ul>

              {/* NovelAI 탭 */}
              <div className={`tab-content ${activeTab === "NovelAI" ? "active" : ""}`}>
                <div className="metadata-section">
                  {/* 긍정 프롬프트 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-positive" className="label-strong">
                        긍정 프롬프트
                      </label>
                      <button
                        onClick={() => copyText(naiPositiveDump)}
                        aria-label="긍정 프롬프트 복사"
                        disabled={isEmpty(naiPositiveDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiPositiveDump}
                    </div>
                  </div>

                  {/* 캐릭터 긍정 프롬프트 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-positive" className="label-strong">
                        캐릭터 긍정 프롬프트
                      </label>
                      <button
                        onClick={() => copyText(naiCharPositiveDump)}
                        aria-label="캐릭터 긍정 프롬프트 복사"
                        disabled={isEmpty(naiCharPositiveDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-char-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharPositiveDump}
                    </div>
                  </div>

                  {/* 부정 프롬프트 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-negative" className="label-strong">
                        부정 프롬프트
                      </label>
                      <button
                        onClick={() => copyText(naiNegativeDump)}
                        aria-label="부정 프롬프트 복사"
                        disabled={isEmpty(naiNegativeDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiNegativeDump}
                    </div>
                  </div>

                  {/* 캐릭터 부정 프롬프트 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-negative" className="label-strong">
                        캐릭터 부정 프롬프트
                      </label>
                      <button
                        onClick={() => copyText(naiCharNegativeDump)}
                        aria-label="캐릭터 부정 프롬프트 복사"
                        disabled={isEmpty(naiCharNegativeDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-char-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharNegativeDump}
                    </div>
                  </div>

                  {/* NAI 모델 및 설정 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-model" className="label-strong">
                        NAI 모델 및 설정
                      </label>
                      <button
                        onClick={() => copyText(naiModelDump)}
                        aria-label="NAI 모델 및 설정 복사"
                        disabled={isEmpty(naiModelDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-model"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiModelDump}
                    </div>
                  </div>

                  {/* Stealth PNG 메타데이터 */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-stealth" className="label-strong">
                        Stealth PNG 메타데이터
                      </label>
                      <button
                        onClick={() => copyText(naiStealthDump)}
                        aria-label="Stealth PNG 메타데이터 복사"
                        disabled={isEmpty(naiStealthDump)}
                        className="btn btn-primary"
                      >
                        복사
                      </button>
                    </div>
                    <div
                      id="nai-stealth"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiStealthDump}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="법적 고지 링크">
          <Link to="/terms" className="footer-link">
            이용약관
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/privacy" className="footer-link">
            개인정보처리방침
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default EXIFPage;